import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import React, { useEffect } from 'react';
import RouteHelpers from 'helpers/routes';
import { validateToken } from 'actions/userTokens';
import { useNavigate } from 'react-router-dom';
import { useRouteParams } from 'hooks/useRouteParams';
import { Button } from '@teachstonellc/component-library';
import { AlertCircleFilled } from '@teachstonellc/component-library/dist/icons';
import Invite from 'components/Tokens/View/Invite';
import PaymentRequest from 'components/Tokens/View/PaymentRequest';
import ContainerWithLogo from 'components/ContainerWithLogo';
import { MessageContainer, Message, Container } from './Styled';
import useCurrentUser from 'hooks/useCurrentUser';
import useLogout from 'hooks/useLogout';
import useProductKey from 'components/ActivateProductKey/useProductKey';
import TokenRedemptionDialog from 'components/ActivateProductKey/TokenRedemptionDialog';

type URLParams = {
  token: string;
};

const View: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const pKeyRequest = useProductKey();
  const handleLogout = useLogout();
  const { token } = useRouteParams<URLParams>();
  const { run, data: userToken, isPending, isSuccess, isError } = useAsync();
  const isInvite = userToken?.type_group === 'invite';
  const isProductKey = userToken?.type_group === 'product_key';

  useEffect(() => {
    run(validateToken(token), {
      onSuccess: data => {
        if (data?.type_group === 'product_key') {
          if (!currentUser?.id) {
            // when user is not logged in
            navigate(RouteHelpers.getPath('users-activate-pk'), {
              state: { token: data?.token }
            });
          }
        } else if (
          data?.type_name === 'subscription_invite' &&
          currentUser?.id &&
          (currentUser.id === data?.user_id ||
            currentUser.email?.toLowerCase() === data?.email?.toLowerCase())
        ) {
          navigate(
            RouteHelpers.getPath('user-token-invitation', {
              token: data.token
            }),
            { replace: true }
          );
        } else {
          handleLogout();
        }
      }
    });
  }, [run, token, handleLogout, currentUser?.id, currentUser?.email, navigate]);

  return (
    <Container>
      <TokenRedemptionDialog
        confirmation={pKeyRequest.pendingConfirmation}
        isLoading={pKeyRequest.isLoading}
        onConfirm={pKeyRequest.confirmTokenRedemption}
        onCancel={pKeyRequest.cancelTokenRedemption}
      />
      {isPending && (
        <MessageContainer>
          <i className="fa-solid fa-spinner animate-spin text-4xl" />
          <Message>{i18n.ft(messages.pleaseWait)}</Message>
        </MessageContainer>
      )}

      {isError && (
        <ContainerWithLogo>
          <MessageContainer>
            <AlertCircleFilled size={64} className="text-ds-icon-negative" />
            <Message
              dangerouslySetInnerHTML={{
                __html: i18n.ft(messages.expiredLink)
              }}
            />
            <Button variant="secondary" onClick={() => navigate('/')}>
              {i18n.ft(messages.goBack)}
            </Button>
          </MessageContainer>
        </ContainerWithLogo>
      )}

      {isSuccess && isInvite && <Invite userToken={userToken} />}

      {isSuccess && userToken.type_name === 'training_payment_request' && (
        <PaymentRequest token={userToken} />
      )}

      {isSuccess && isProductKey && currentUser?.id && (
        <Container>
          <ContainerWithLogo>
            <MessageContainer>
              <Message>
                <strong>{i18n.ft(messages.product)}:</strong>{' '}
                {userToken.friendly_name}
              </Message>
              <Message
                dangerouslySetInnerHTML={{
                  __html: i18n.ft(messages.confirmationText, {
                    email: currentUser.email
                  })
                }}
              />
              <Button
                fullWidth
                type="submit"
                variant="primary"
                loading={pKeyRequest.isLoading}
                disabled={pKeyRequest.isLoading || pKeyRequest.isError}
                onClick={() => pKeyRequest.submit(userToken.token)}
              >
                {i18n.ft(messages.confirmButton)}
              </Button>
              {pKeyRequest.error && (
                <div className="flex column">
                  <Message className="text-red-600">
                    {pKeyRequest.error}
                  </Message>
                </div>
              )}
            </MessageContainer>
          </ContainerWithLogo>
        </Container>
      )}
    </Container>
  );
};

export default View;
